/**
 * SelectTool — Click/drag selection
 * Per PLAN.md interactions/SelectTool.js
 */

import { eventBus, Events } from '../core/EventBus.js';
import { rectsOverlap } from '../utils/geometry.js';

export class SelectTool {
    /**
     * @param {import('../core/Canvas.js').Canvas} canvas 
     * @param {import('../models/CanvasDocument.js').CanvasDocument} document 
     */
    constructor(canvas, document) {
        this.canvas = canvas;
        this.document = document;
        this.isActive = true;

        // Drag state
        this.isDragging = false;
        this.isBoxSelecting = false;
        this.dragStartWorld = null;
        this.dragStartScreen = null;
        this.draggedNodeIds = [];
        this.initialPositions = new Map();

        this.init();
    }

    init() {
        // Listen for canvas clicks
        eventBus.on(Events.CANVAS_CLICK, (e) => {
            if (!this.isActive) return;
            this.handleClick(e);
        });

        // Listen for double-clicks
        eventBus.on(Events.CANVAS_DBLCLICK, (e) => {
            if (!this.isActive) return;
            this.handleDoubleClick(e);
        });
    }

    /**
     * Handle canvas click
     */
    handleClick({ x, y, shiftKey }) {
        const node = this.document.getNodeAtPosition(x, y);

        if (node) {
            if (shiftKey) {
                // Toggle selection
                if (this.canvas.selectedNodeIds.has(node.id)) {
                    this.canvas.selectedNodeIds.delete(node.id);
                } else {
                    this.canvas.selectedNodeIds.add(node.id);
                }
            } else {
                // Single select
                this.canvas.selectNodes([node.id]);
            }
            eventBus.emit(Events.NODE_SELECT, { nodeId: node.id });
        } else {
            // Click on empty space - clear selection AND close editor
            this.canvas.clearSelection();
            eventBus.emit(Events.NODE_DESELECT, {});
            eventBus.emit(Events.EDITOR_CLOSE, {});
        }
    }

    /**
     * Handle double-click (create new node)
     */
    handleDoubleClick({ x, y }) {
        const node = this.document.getNodeAtPosition(x, y);

        if (node) {
            // Edit existing node
            eventBus.emit(Events.EDITOR_OPEN, { nodeId: node.id });
        } else {
            // Request new node creation
            eventBus.emit(Events.NODE_CREATE_REQUEST, { x, y, type: 'latex' });
        }
    }

    /**
     * Start dragging selected nodes
     * @param {number} screenX 
     * @param {number} screenY 
     */
    startDrag(screenX, screenY) {
        this.isDragging = true;
        this.dragStartScreen = { x: screenX, y: screenY };
        this.dragStartWorld = this.canvas.viewport.screenToWorld(screenX, screenY);

        // Store initial positions
        this.draggedNodeIds = Array.from(this.canvas.selectedNodeIds);
        this.initialPositions.clear();
        for (const id of this.draggedNodeIds) {
            const node = this.document.getNode(id);
            if (node) {
                this.initialPositions.set(id, { ...node.position });
            }
        }
    }

    /**
     * Continue dragging
     * @param {number} screenX 
     * @param {number} screenY 
     */
    doDrag(screenX, screenY) {
        if (!this.isDragging) return;

        const currentWorld = this.canvas.viewport.screenToWorld(screenX, screenY);
        const dx = currentWorld.x - this.dragStartWorld.x;
        const dy = currentWorld.y - this.dragStartWorld.y;

        // Move all selected nodes
        for (const id of this.draggedNodeIds) {
            const node = this.document.getNode(id);
            const initial = this.initialPositions.get(id);
            if (node && initial) {
                node.position.x = initial.x + dx;
                node.position.y = initial.y + dy;
            }
        }

        eventBus.emit(Events.RENDER_REQUEST);
    }

    /**
     * End dragging
     * @returns {{deltaX: number, deltaY: number, nodeIds: string[]}}
     */
    endDrag() {
        if (!this.isDragging) return null;

        this.isDragging = false;

        // Calculate total delta for undo
        const firstId = this.draggedNodeIds[0];
        const initial = this.initialPositions.get(firstId);
        const node = this.document.getNode(firstId);

        if (initial && node) {
            return {
                deltaX: node.position.x - initial.x,
                deltaY: node.position.y - initial.y,
                nodeIds: [...this.draggedNodeIds]
            };
        }

        return null;
    }

    /**
     * Start box selection
     * @param {number} screenX 
     * @param {number} screenY 
     */
    startBoxSelect(screenX, screenY) {
        this.isBoxSelecting = true;
        this.canvas.selectionBox = {
            x1: screenX,
            y1: screenY,
            x2: screenX,
            y2: screenY
        };
    }

    /**
     * Update box selection
     * @param {number} screenX 
     * @param {number} screenY 
     */
    doBoxSelect(screenX, screenY) {
        if (!this.isBoxSelecting) return;

        this.canvas.selectionBox.x2 = screenX;
        this.canvas.selectionBox.y2 = screenY;

        eventBus.emit(Events.RENDER_REQUEST);
    }

    /**
     * End box selection
     */
    endBoxSelect() {
        if (!this.isBoxSelecting) return;

        this.isBoxSelecting = false;

        const box = this.canvas.selectionBox;
        if (!box) return;

        // Convert to world coordinates
        const topLeft = this.canvas.viewport.screenToWorld(
            Math.min(box.x1, box.x2),
            Math.min(box.y1, box.y2)
        );
        const bottomRight = this.canvas.viewport.screenToWorld(
            Math.max(box.x1, box.x2),
            Math.max(box.y1, box.y2)
        );

        const selectionRect = {
            x: topLeft.x,
            y: topLeft.y,
            width: bottomRight.x - topLeft.x,
            height: bottomRight.y - topLeft.y
        };

        // Find all nodes in selection
        const selectedIds = [];
        for (const node of this.document.getAllNodes()) {
            if (rectsOverlap(selectionRect, node.getBounds())) {
                selectedIds.push(node.id);
            }
        }

        this.canvas.selectNodes(selectedIds);
        this.canvas.selectionBox = null;

        eventBus.emit(Events.RENDER_REQUEST);
    }

    /**
     * Activate/deactivate this tool
     * @param {boolean} active 
     */
    setActive(active) {
        this.isActive = active;
    }
}
